import type { TaxonWithConfidenceDto } from '@/api/TaxonomyApi/generated/model';
import { ListItem } from '@/components/List/ListItem';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { Ionicons } from '@expo/vector-icons';
import { SizableText, XStack, YStack, useTheme } from 'tamagui';
import { TrySearchRow } from '../components/TrySearchRow';
import { SpeciesAiResultBadge } from './SpeciesAiResultBadge';
import { formatConfidence } from './SpeciesAiSearchScreen.utils';
import { resolveScientificDisplayName } from '@screens/SpeciesSearch/SpeciesTaxonomyScreen/speciesTaxonomy.utils';

interface SpeciesAiResultsProps {
  bestMatch?: TaxonWithConfidenceDto | null;
  otherMatches?: TaxonWithConfidenceDto[];
  onSelect: (scientificName: string) => void;
  onTrySearch: () => void;
}

export const SpeciesAiResults = ({
  bestMatch,
  otherMatches = [],
  onSelect,
  onTrySearch,
}: SpeciesAiResultsProps): React.ReactElement => {
  const { t, language } = useAppTranslation();
  const theme = useTheme();
  const totalMatches = (bestMatch ? 1 : 0) + otherMatches.length;
  const hasMatches = totalMatches > 0;

  const renderMatchItem = (
    item: TaxonWithConfidenceDto,
    variant: 'best' | 'other',
  ): React.ReactElement => {
    const isBest = variant === 'best';
    const vernacularNames = item.vernacularNames ?? [];
    const localizedName = vernacularNames.find(
      (vernacularName) => vernacularName.language === language,
    )?.name;
    const scientificName = resolveScientificDisplayName(item);
    const label = localizedName ?? vernacularNames[0]?.name ?? scientificName;

    const confidence = formatConfidence(item.confidence);

    return (
      <ListItem
        key={item.code}
        onPress={() => onSelect(scientificName)}
        title={label}
        titleAfter={<SpeciesAiResultBadge label={confidence} />}
        subtitle={scientificName}
        iconAfter={
          <Ionicons name="chevron-forward" size={18} color={theme.color?.val} />
        }
        variant={isBest ? 'primary' : 'secondary'}
      />
    );
  };

  if (!hasMatches) {
    return (
      <YStack alignItems="center" gap="$3" width="100%">
        <Ionicons name="sparkles" size={24} color={theme.brand?.val} />
        <SizableText size="$h6" fontFamily="$bold" textAlign="center">
          {t('speciesSearch.ai.noResultsTitle')}
        </SizableText>
        <SizableText size="$2" color="$secondary75" textAlign="center">
          {t('speciesSearch.ai.noResultsDescription')}
        </SizableText>
        <TrySearchRow onTrySearch={onTrySearch} />
      </YStack>
    );
  }

  return (
    <YStack alignItems="center" gap="$6" width="100%">
      <YStack alignItems="center" gap="$1">
        <SizableText size="$h6" fontFamily="$bold" textAlign="center">
          {t('speciesSearch.ai.matchesTitle', { count: totalMatches })}
        </SizableText>
        <SizableText size="$2" color="$secondary75" textAlign="center">
          {t('speciesSearch.ai.matchesSubtitle')}
        </SizableText>
      </YStack>

      {bestMatch ? (
        <YStack gap="$2" width="100%">
          <XStack justifyContent="center">
            <SpeciesAiResultBadge
              icon={
                <Ionicons name="sparkles" size={16} color={theme.info?.val} />
              }
              label={t('speciesSearch.ai.bestMatch')}
            />
          </XStack>

          {renderMatchItem(bestMatch, 'best')}
        </YStack>
      ) : null}

      {otherMatches.length > 0 ? (
        <YStack gap="$2" width="100%">
          <SizableText size="$2" color="$secondary75" textAlign="center">
            {t('speciesSearch.ai.otherMatches')}
          </SizableText>
          {otherMatches.map((item) => renderMatchItem(item, 'other'))}
        </YStack>
      ) : null}

      <TrySearchRow onTrySearch={onTrySearch} />
    </YStack>
  );
};
